import { useQuery } from '@tanstack/react-query'
import {
  fetchAllPendingOpeningEvents,
  fetchConfigDoor,
  fetchStoreOpeningState,
} from '../queries/door.queries'

export const useConfigDoor = (storeId: number) =>
  useQuery({
    queryKey: ['doorConfig', storeId],
    queryFn: () => fetchConfigDoor(storeId),
  })

export const useStoreOpeningState = (storeId: number) =>
  useQuery({
    queryKey: ['storeOpeningState', storeId],
    queryFn: () => fetchStoreOpeningState(storeId),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 25_000,
  })

export type PendingOpeningEventsScope = { stores: number[] } | { network: true }

/**
 * Demandes d'ouverture en attente d'un périmètre : une sélection de boutiques (plafonnée à 100
 * ids, limite de l'endpoint) ou tout le réseau — sans filtre HTTP, donc sans plafond.
 */
export const usePendingOpeningEventsForScope = (
  scope: PendingOpeningEventsScope,
  refetchInterval: number | false = false,
) => {
  const normalizedStoreIds =
    'network' in scope
      ? null
      : [...new Set(scope.stores.filter((storeId) => Number.isSafeInteger(storeId) && storeId > 0))]
          .sort((left, right) => left - right)
          .slice(0, 100)
  return useQuery({
    queryKey:
      normalizedStoreIds === null
        ? ['pendingOpeningEvents', 'network']
        : ['pendingOpeningEvents', 'stores', normalizedStoreIds],
    queryFn: () => {
      if (normalizedStoreIds === null) return fetchAllPendingOpeningEvents()
      return normalizedStoreIds.length > 0
        ? fetchAllPendingOpeningEvents(normalizedStoreIds)
        : Promise.resolve([])
    },
    refetchInterval,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  })
}

export const usePendingOpeningEventsForStores = (
  storeIds: number[],
  refetchInterval: number | false = false,
) => usePendingOpeningEventsForScope({ stores: storeIds }, refetchInterval)
